import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { trackWishlistError, WishlistError, WishListEventType } from '@jsmdg/tracking';
import { useWishlist, WishlistErrorTypes } from '@jsmdg/wishlist-library';
import { type WishlistResponseError } from '@jsmdg/wishlist-library/dist/esm/client/types';
import { useSnackbarContext, WishlistButton, type WishlistButtonType } from '@jsmdg/yoshi';
import { type ButtonSize } from '@jsmdg/yoshi/dist/enums';
import { NotificationVariant } from '@jsmdg/yoshi/dist/enums/notification';
import { type Price } from '../../../shared/price';
import { type Product } from '../../../shared/types';
import { useBuybox } from '../../hooks/useBuybox';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { type PurchaseType } from '../../types/enums/purchaseType';
import { type ProductDetailContext } from '../../types/types';
import {
    trackToggleWishlistItem,
    trackToggleWishlistItemForEec,
} from './AddToWishlistButton.tracking';

const messages = defineMessages({
    screenReaderTextForWishlistRemove: {
        defaultMessage: 'Von der Wunschliste entfernen',
    },
    screenReaderTextForWishlistAdd: {
        defaultMessage: 'Zur Wunschliste hinzufügen',
    },
    tooManyWishlistArticlesError: {
        defaultMessage:
            'Die maximale Anzahl an Erlebnissen wurde erreicht – bitte entferne Artikel, um Platz für neue zu schaffen.',
    },
});

type ToggleWishlistItemArgs = {
    a11yCloseButtonText: string;
    intl: IntlShape;
    isProductInWishlist: boolean;
    openSnackbar: ReturnType<typeof useSnackbarContext>['openSnackbar'];
    price: Price;
    product: Product;
    purchaseType?: PurchaseType;
    toggleWishlistItem: (productId: string) => Promise<unknown>;
};

async function toggleWishlistItemOnClick({
    a11yCloseButtonText,
    intl,
    isProductInWishlist,
    openSnackbar,
    price,
    product,
    purchaseType,
    toggleWishlistItem,
}: ToggleWishlistItemArgs): Promise<void> {
    try {
        await toggleWishlistItem(product.productId);

        const trackingType = isProductInWishlist
            ? WishListEventType.RemoveFromWishlist
            : WishListEventType.AddToWishlist;

        trackToggleWishlistItem(product.productId, trackingType, product.title);

        if (!isProductInWishlist) {
            trackToggleWishlistItemForEec(product, price, purchaseType, isProductInWishlist);
        }
    } catch (error) {
        const responseMessage = (error as WishlistResponseError).response?.data?.message;
        const errorMessage = (error as Error).message;
        const isCountExceeded =
            responseMessage === WishlistErrorTypes.COUNT_EXCEEDED ||
            errorMessage === WishlistErrorTypes.COUNT_EXCEEDED;

        if (isCountExceeded) {
            openSnackbar({
                content: intl.formatMessage(messages.tooManyWishlistArticlesError),
                variant: NotificationVariant.Error,
                a11yTextCloseButton: a11yCloseButtonText,
            });

            trackWishlistError(
                WishlistError.MoreThan99Products,
                intl.formatMessage(messages.tooManyWishlistArticlesError),
            );
            return;
        }

        logToSentry(error as Error);
    }
}

const AddToWishlistButton = ({
    a11yCloseButtonText,
    buttonClasses,
    dataTestId,
    isTransparent,
    size,
    wishlistButtonType,
}: {
    readonly buttonClasses: string;
    readonly dataTestId: string;
    readonly a11yCloseButtonText: string;
    readonly isTransparent: boolean;
    readonly wishlistButtonType: WishlistButtonType;
    readonly size?: ButtonSize;
}): JSX.Element => {
    const product = useProduct();
    const { price } = usePrice();
    const { purchaseType } = useBuybox();

    const { customerState, initialWishlist } = useFragmentContext<ProductDetailContext>();

    const { isInWishlist, toggleWishlistItem } = useWishlist(customerState, initialWishlist);
    const intl = useIntl();

    const { openSnackbar } = useSnackbarContext();
    const isProductInWishlist = isInWishlist(product.productId);

    return (
        <WishlistButton
            size={size}
            onClick={() =>
                toggleWishlistItemOnClick({
                    a11yCloseButtonText,
                    intl,
                    isProductInWishlist,
                    openSnackbar,
                    price,
                    product,
                    purchaseType,
                    toggleWishlistItem,
                })
            }
            isActive={isProductInWishlist}
            className={buttonClasses}
            a11yText={intl.formatMessage(
                isProductInWishlist
                    ? messages.screenReaderTextForWishlistRemove
                    : messages.screenReaderTextForWishlistAdd,
            )}
            data-testid={dataTestId}
            isTransparent={isTransparent}
            wishlistButtonType={wishlistButtonType}
        />
    );
};

export { AddToWishlistButton };
